import {
  type ComponentType,
  type Dispatch,
  Fragment,
  type ReactNode,
  type SetStateAction,
  createElement,
} from "react";
import * as Yup from "yup";

import {
  getPerformanceTabMetadata,
  getPositiveIntegerSchema,
  strategies,
} from "metabase/admin/performance/constants/complex";
import type { ModelWithClearableCache } from "metabase/admin/performance/types";
import { PerformanceTabId } from "metabase/admin/performance/types";
import type { MetricPageParams, MetricUrls } from "metabase/metrics/types";
import { PluginPlaceholder } from "metabase/plugins/components/PluginPlaceholder";
import type { ModalOverlayProps, StackProps } from "metabase/ui";
import type Question from "metabase-lib/v1/Question";
import type {
  CacheableDashboard,
  CacheableModel,
  Card,
} from "metabase-types/api";
import { CacheDurationUnit } from "metabase-types/api";

// Types
export type InvalidateNowButtonProps = {
  targetId: number;
  targetModel: ModelWithClearableCache;
  targetName: string;
};

export type SidebarCacheSectionProps = {
  item: CacheableDashboard | Question;
  model: CacheableModel;
  setPage: Dispatch<SetStateAction<"default" | "caching">>;
};

export type SidebarCacheFormProps = {
  item: CacheableDashboard | Question;
  model: CacheableModel;
  isOpen: boolean;
  withOverlay?: boolean;
  overlayProps?: ModalOverlayProps;
  onClose: () => void;
  onBack: () => void;
} & StackProps;

export type PreemptiveCachingSwitchProps = {
  handleSwitchToggle: () => void;
};

export interface MetricSettingsPageProps {
  params: MetricPageParams;
  urls?: MetricUrls;
  renderBreadcrumbs?: (card: Card) => ReactNode;
  showAppSwitcher?: boolean;
  showDataStudioLink?: boolean;
}

const durationStrategyValidationSchema = Yup.object({
  type: Yup.string().equals(["duration"]),
  duration: getPositiveIntegerSchema().default(24),
  unit: Yup.string().oneOf(Object.values(CacheDurationUnit).map(String)),
  refresh_automatically: Yup.boolean().nullable().default(false),
});

const getOssGranularPerformanceTabMetadata = () => {
  const metadata = getPerformanceTabMetadata();
  const cacheSettingsTab = metadata.find(
    ({ key }) => key === "performance-cache-settings",
  );
  const databasesTab = metadata.find(
    ({ key }) => key === "performance-databases",
  );
  const modelsTab = metadata.find(({ key }) => key === "performance-models");

  return [
    cacheSettingsTab,
    databasesTab,
    {
      name: "Dashboard caching",
      path: "/admin/performance/dashboards",
      key: "performance-dashboards",
      tabId: PerformanceTabId.Dashboards,
    },
    {
      name: "Question caching",
      path: "/admin/performance/questions",
      key: "performance-questions",
      tabId: PerformanceTabId.Questions,
    },
    modelsTab,
  ].filter(Boolean);
};

const GranularControlsExplanation = () =>
  createElement(
    Fragment,
    null,
    "\u00A0You can set up one caching policy for all your databases, or set up caching policies specific to each database.",
  );

const hasQuestionCacheSection = (question: Question) => {
  const type = question.type();

  return (
    type !== "model" &&
    (question.canWrite() || question.lastQueryStart() != null)
  );
};

const StrategyFormLauncherPanel = (props: any) => {
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const mod = require("metabase/admin/performance/oss-granular-caching");
  const Component = mod.StrategyFormLauncherPanel;
  return createElement(Component, props);
};

const SidebarCacheSection = (props: SidebarCacheSectionProps) => {
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const mod = require("metabase/admin/performance/oss-granular-caching");
  const Component = mod.SidebarCacheSection;
  return createElement(Component, props);
};

const SidebarCacheForm = (props: SidebarCacheFormProps) => {
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const mod = require("metabase/admin/performance/oss-granular-caching");
  const Component = mod.SidebarCacheForm;
  return createElement(Component, props);
};

const InvalidateNowButton = (props: InvalidateNowButtonProps) => {
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const mod = require("metabase/admin/performance/oss-granular-caching");
  const Component = mod.InvalidateNowButton;
  return createElement(Component, props);
};

const StrategyEditorForQuestionsAndDashboards = (props: any) => {
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const mod = require("metabase/admin/performance/oss-granular-caching");
  const Component = mod.StrategyEditorForQuestionsAndDashboards;
  return createElement(Component, props);
};

const getDefaultPluginCaching = () => ({
  isGranularCachingEnabled: () => true,
  StrategyFormLauncherPanel: StrategyFormLauncherPanel as any,
  GranularControlsExplanation: GranularControlsExplanation as any,
  SidebarCacheSection:
    SidebarCacheSection as ComponentType<SidebarCacheSectionProps>,
  SidebarCacheForm: SidebarCacheForm as ComponentType<SidebarCacheFormProps>,
  InvalidateNowButton:
    InvalidateNowButton as ComponentType<InvalidateNowButtonProps>,
  hasQuestionCacheSection: hasQuestionCacheSection as (
    _question: Question,
  ) => boolean,
  canOverrideRootStrategy: true,
  strategies: {
    inherit: strategies.inherit,
    duration: {
      label: () => "Duration: keep the cache for a fixed amount of time",
      shortLabel: () => "Duration",
      validationSchema: durationStrategyValidationSchema,
    },
    ttl: strategies.ttl,
    nocache: strategies.nocache,
  },
  DashboardAndQuestionCachingTab: PluginPlaceholder as any,
  StrategyEditorForQuestionsAndDashboards:
    StrategyEditorForQuestionsAndDashboards as any,
  getTabMetadata: getOssGranularPerformanceTabMetadata,
  PreemptiveCachingSwitch:
    PluginPlaceholder as ComponentType<PreemptiveCachingSwitchProps>,
  MetricCachingPage:
    PluginPlaceholder as ComponentType<MetricSettingsPageProps>,
});

export const PLUGIN_CACHING = getDefaultPluginCaching();

/**
 * @internal Do not call directly. Use the main reinitialize function from metabase/plugins instead.
 */
export function reinitialize() {
  Object.assign(PLUGIN_CACHING, getDefaultPluginCaching());
}
